import { Navigate, Outlet, useLocation } from 'react-router'

import { Spinner } from '@/components/ui/spinner'
import { homePathFor, useSession } from '@/lib/session'
import type { Role } from '@/lib/types'

export function FullPageLoader() {
  return (
    <div className="flex min-h-dvh items-center justify-center text-ink-3">
      <Spinner className="size-5" />
    </div>
  )
}

export const SET_PASSWORD_PATH = '/portal/set-password'

/** Renders child routes only for the given roles; otherwise redirects sensibly. */
export function RequireRole({ roles, loginPath }: { roles: Role[]; loginPath: string }) {
  const { data: user, isPending } = useSession()
  const location = useLocation()

  if (isPending) return <FullPageLoader />
  if (!user) {
    const next = encodeURIComponent(location.pathname + location.search)
    return <Navigate to={`${loginPath}?next=${next}`} replace />
  }
  if (!roles.includes(user.role)) return <Navigate to={homePathFor(user)} replace />
  // A student must choose a password before using the portal (the API enforces it too).
  if (user.needs_password && location.pathname !== SET_PASSWORD_PATH) {
    const next = encodeURIComponent(location.pathname + location.search)
    return <Navigate to={`${SET_PASSWORD_PATH}?next=${next}`} replace />
  }
  return <Outlet />
}

/** Auth screens: if already signed in, go straight to the right home. */
export function GuestOnly() {
  const { data: user, isPending } = useSession()
  if (isPending) return <FullPageLoader />
  if (user) return <Navigate to={homePathFor(user)} replace />
  return <Outlet />
}

export function RootRedirect() {
  const { data: user, isPending } = useSession()
  if (isPending) return <FullPageLoader />
  return <Navigate to={homePathFor(user)} replace />
}

/** Only reads `next` when it is a same-site path (prevents open redirects). */
export function safeNext(search: string, fallback: string): string {
  const next = new URLSearchParams(search).get('next')
  return next && next.startsWith('/') && !next.startsWith('//') ? next : fallback
}
